import { ChargerSchedule } from "../store/actions/scheduling-actions";

// ────────────────────────────────────────────────────────────────────────────────
// Charger Status/Error Types (for OCPP chargers)
// ────────────────────────────────────────────────────────────────────────────────

export type ChargerErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

// Error info from lookup table (nested in chargerState)
export interface ChargerErrorInfo {
    error_code: string;
    description: string;
    severity: ChargerErrorSeverity;
    user_message: string;
    recommended_action: string;
}

// Charger state from OCPP (nested object in some responses like getActiveChargeData)
export interface ChargerStateInfo {
    id?: number;
    charger_id?: string;
    connector_status: string;
    vehicle_connected: boolean | null;
    vehicle_charging: boolean | null;
    current_power_w: number | null;
    current_energy_kwh: number | null;
    active_order_id: string | null;
    active_history_id: string | null;
    last_seen: string | null;
    last_status_update: string | null;
    error_code: string | null;
    error_info: string | null;
    vendor_id: string | null;
    vendor_error_code: string | null;
    created_at?: string;
    updated_at?: string;
    // Nested error info from lookup table
    errorInfo: ChargerErrorInfo | null;
    
    // Fault recovery fields
    interrupted_transaction_id: string | null;  // OCPP transaction ID that was interrupted
    interrupted_at: string | null;              // When the fault occurred
    interrupted_id_tag: string | null;          // The id_tag needed for recovery
    recovery_attempts: number | null;           // Counter for recovery attempts
    last_recovery_attempt: string | null;       // Timestamp of last attempt
}

// Status display helper type
export type ChargerDisplayStatus = 
    | 'Available' 
    | 'In Use' 
    | 'Faulted'
    | 'Charger Error' 
    | 'Unavailable' 
    | 'Charger Offline'
    | 'Pending' 
    | 'Preparing' 
    | 'Vehicle Connected'
    | 'Finishing' 
    | 'Reserved' 
    | 'SuspendedEV' 
    | 'SuspendedEVSE';

// Maps OCPP connector statuses to display-friendly values
export const mapConnectorStatusToDisplay = (connectorStatus: string | null | undefined): ChargerDisplayStatus => {
    if (!connectorStatus) return 'Available';
    
    const statusMap: Record<string, ChargerDisplayStatus> = {
        'Available': 'Available',
        'Preparing': 'Vehicle Connected',
        'Charging': 'In Use',
        'SuspendedEV': 'SuspendedEV',
        'SuspendedEVSE': 'SuspendedEVSE',
        'Finishing': 'Finishing',
        'Reserved': 'Reserved',
        'Unavailable': 'Unavailable',
        'Faulted': 'Charger Error',
    };
    
    return statusMap[connectorStatus] || 'Available';
};

// Gets the color for a given status and severity
export const getStatusColor = (
    status: ChargerDisplayStatus, 
    severity?: ChargerErrorSeverity | string | null
): string => {
    // For charger error states, use severity-based colors
    if (status === 'Charger Error' && severity) {
        const severityColors: Record<string, string> = {
            'info': '#3B82F6',      // Blue
            'warning': '#F59E0B',   // Amber
            'error': '#EF4444',     // Red
            'critical': '#DC2626',  // Dark red
        };
        return severityColors[severity] || '#EF4444';
    }
    
    // Default status colors
    const statusColors: Record<ChargerDisplayStatus, string> = {
        'Available': '#51B868',     // Green
        'In Use': '#F97777',        // Coral red
        'Faulted': '#EF4444',       // Red
        'Charger Error': '#EF4444', // Red
        'Unavailable': '#6B7280',   // Gray
        'Charger Offline': '#6B7280', // Gray
        'Pending': '#FFA500',       // Orange
        'Vehicle Connected': '#3B82F6', // Blue
        'Preparing': '#3B82F6', // Blue
        'Finishing': '#8B5CF6',     // Purple
        'Reserved': '#F59E0B',      // Amber
        'SuspendedEV': '#F59E0B',   // Amber
        'SuspendedEVSE': '#F59E0B', // Amber
    };
    
    return statusColors[status] || '#6B7280';
};

// Check if a charger has error info that should be shown on tap
export const hasChargerErrorInfo = (charger: ChargerAttributes): boolean => {
    // Check flat fields first
    if (charger.error_user_message || charger.error_description) return true;
    // Check nested chargerState
    if (charger.chargerState?.errorInfo) return true;
    return false;
};

// Get error info from charger (checks both flat fields and nested chargerState)
export const getChargerErrorInfo = (charger: ChargerAttributes): ChargerErrorInfo | null => {
    // Prefer nested chargerState.errorInfo if available
    if (charger.chargerState?.errorInfo) {
        return charger.chargerState.errorInfo;
    }
    // Fall back to constructing from flat fields
    if (charger.error_user_message || charger.error_description) {
        return {
            error_code: charger.error_code || 'Unknown',
            description: charger.error_description || '',
            severity: (charger.error_severity as ChargerErrorSeverity) || 'error',
            user_message: charger.error_user_message || 'An error occurred',
            recommended_action: charger.error_recommended_action || '',
        };
    }
    return null;
};

// Check if a status should show detailed error info on tap
export const isStatusTappable = (status: ChargerDisplayStatus, charger: ChargerAttributes): boolean => {
    if (['Available', 'In Use', 'Charger Offline'].includes(status)) return false;
    return hasChargerErrorInfo(charger);
};

// Check if charger is offline based on connected field
export const isChargerOffline = (charger: ChargerAttributes): boolean => {
    return charger.connected === false;
};

interface ChargerFinancials {
    financialsId?: number;
    cost_kwh_peak?: number;
    cost_kwh_off_peak?: number;
    price_kwh_peak: number;
    price_kwh_off_peak: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ChargerAttributes {
    uuid: string | undefined;
    manufacturer_name?: string;
    charger_model?: string;
    unit_serial: string;
    meander_serial?: string;
    charger_type?: string;
    plug_type?: string;
    description?: string;
    environment?: string;
    address?: string;
    provider?: string;
    charging_capacity?: number;
    install_date?: Date; // Assuming install_date is in the format YYYY-MM-DD
    contact_phone?: string;
    contact_email?: string;
    access_instructions?: string;
    availability_times?: string;
    booked_times?: string;
    isOccupied?: number;
    power_provider?: number;
    grace_period?: number;
    registered_user: string;
    total_uptime: number;
    lifetime_kwh: number;
    manufacturer_device_id: string;
    lat: number;
    lng: number;
    price_hourly: number;
    name: string;
    currency: string;
    updatedAt: Date;
    createdAt: Date;
    full_address: string;
    street_number: string;
    street_name: string;
    zip_code: string;
    city: string;
    state: string;
    country: string;
    price_hourly_peak: number;
    price_hourly_off_peak: number;
    peak_selection: string;
    isSelected: boolean;
    status: string | null;
    totalDistance?: number;
    is_enabled: boolean;
    financials: ChargerFinancials;
    can_schedule: boolean;
    charger_email: string;
    message?: string;
    // Image metadata (optional - used by cache service for optimization)
    customImageUrl?: string; // Cached thumbnail URL
    hasCustomImage?: boolean; // Quick check without loading
    imageLastUpdated?: Date; // For cache invalidation
    
    // Revenue split data (from backend)
    hasRevenueSplits?: boolean; // Quick flag to show badge
    activeSplitCount?: number; // Number of active splits
    grossRevenue?: number; // Total revenue before splits (this month)
    netRevenue?: number; // Revenue after splits (this month)
    splitDeductions?: number; // Total deducted for splits (this month)
    activeSplits?: Array<{
        splitId: string;
        partnerUsername: string;
        partnerEmail?: string;
        splitPercentage: number;
    }>;
    
    // Partner permissions
    permissions?: Array<'read' | 'write' | 'meander' | 'split' | 'observer'>;
    
    // Registration status for pre-registered chargers
    registration_status?: string;
    
    // OCPP charger real-time status fields (flat from backend)
    ocpp_connector_status?: string | null;
    vehicle_connected?: boolean | null;
    vehicle_charging?: boolean | null;
    current_power_w?: number | null;
    current_energy_kwh?: number | null;
    last_seen?: string | null;
    
    // OCPP error information (flat from backend)
    error_code?: string | null;
    error_description?: string | null;
    error_user_message?: string | null;
    error_severity?: ChargerErrorSeverity | string | null;
    error_recommended_action?: string | null;
    vendor_id?: string | null;
    vendor_error_code?: string | null;
    error_info?: string | null;
    
    // Nested chargerState object (for OCPP chargers, may be included in some responses)
    chargerState?: ChargerStateInfo | null;
    
    // Charger connectivity status
    connected?: boolean | null;
    connected_changed_time?: string | null;
}

export const createEmptyCharger = (overrides?: Partial<ChargerAttributes>): ChargerAttributes => ({
    uuid: '',
    manufacturer_name: '',
    charger_model: '',
    unit_serial: '',
    meander_serial: '',
    charger_type: '',
    plug_type: '',
    description: '',
    environment: '',
    address: '',
    provider: '',
    charging_capacity: 0,
    install_date: new Date(),
    contact_phone: '',
    contact_email: '',
    access_instructions: '',
    availability_times: '',
    booked_times: '',
    isOccupied: 0,
    power_provider: 0,
    grace_period: 0,
    registered_user: '',
    total_uptime: 0,
    lifetime_kwh: 0,
    manufacturer_device_id: '',
    lat: 0,
    lng: 0,
    price_hourly: 0,
    currency: '',
    updatedAt: new Date(),
    createdAt: new Date(),
    name: '',
    price_hourly_peak: 0,
    price_hourly_off_peak: 0,
    peak_selection: '',
    full_address: '',
    street_number: '',
    street_name: '',
    zip_code: '',
    city: '',
    state: '',
    country: '',
    isSelected: false,
    status: null,
    totalDistance: 0,
    is_enabled: true,
    financials: {
        price_kwh_peak: 0,
        price_kwh_off_peak: 0,
    },
    can_schedule: false,
    charger_email: '',
    ...overrides
});

export type ChargerManufacturerInfo = {
    name: string | undefined,
    charge_rate_amps: number,
    vehicle_connected: boolean,
    vehicle_charging: boolean,
    charger_on?: boolean,
    manufacturer: string,
    message?: string,
    connected?: boolean,
    connected_changed_time?: string,
    evse_gun_type?: string,
}

export type ChargerUsageData = {
    peakTimeSeconds: number,
    offPeakTimeSeconds: number,
    peakKwh: number,
    offPeakKwh: number
}

export type ActiveChargeStatus = {
    chargerCarData: ChargerManufacturerInfo,
    chargerState?: ChargerStateInfo | null,
    usageData: ChargerUsageData | null,
    schedule: ChargerSchedule | null,
    peakPrice: number,
    offPeakPrice: number,
    message: string,
    chargerConnected?: boolean | null,
    connectedChangedTime?: string | null,
    dataIsStale?: boolean,
}